import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null while loading | object | false
  const [error, setError] = useState("");

  const refreshMe = useCallback(async () => {
    try {
      // Suppress 401 console error from initial unauthenticated probe
      const { data } = await api.get("/auth/me", {
        validateStatus: (s) => (s >= 200 && s < 300) || s === 401,
      });
      if (data && data.id) {
        setUser(data);
        return data;
      }
      setUser(false);
      return null;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      // also login to capture token
      await login(payload.email, payload.password);
      return data;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout is best-effort — even if the server call fails (e.g. offline),
      // we still want to clear client state below.
      console.warn("Logout request failed, clearing local session anyway:", err);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
